import { IChartEngine } from '@smartcharts/chart-web';
import { TQuote } from '@smartcharts/core';
import { PlaybackScheduler, PlaybackSpeed } from './Scheduler';

export type ReplayMode = 'live' | 'paused' | 'replay';

export interface ReplayControllerOptions {
  engine: IChartEngine;
  getQuotes: () => TQuote[];
  onRestoreLive?: () => void;
}

export interface ReplayState {
  mode: ReplayMode;
  currentEpoch: number | null;
  playbackSpeed: PlaybackSpeed;
  isPlaying: boolean;
  startEpoch: number | null;
  endEpoch: number | null;
  progress: number;
}

export class ReplayController {
  private engine: IChartEngine;
  private getQuotesFn: () => TQuote[];
  private onRestoreLiveFn?: () => void;
  private scheduler = new PlaybackScheduler({ speed: 1 });
  private mode: ReplayMode = 'live';
  private playbackSpeed: PlaybackSpeed = 1;
  private isPlaying = false;
  private currentEpoch: number | null = null;
  private startEpoch: number | null = null;
  private endEpoch: number | null = null;
  private playbackTimer?: ReturnType<typeof setTimeout>;
  private idx = 0;

  constructor(opts: ReplayControllerOptions){
    this.engine = opts.engine;
    this.getQuotesFn = opts.getQuotes;
    this.onRestoreLiveFn = opts.onRestoreLive;
  }

  getState(): ReplayState {
    return {
      mode: this.mode,
      currentEpoch: this.currentEpoch,
      playbackSpeed: this.playbackSpeed,
      isPlaying: this.isPlaying,
      startEpoch: this.startEpoch,
      endEpoch: this.endEpoch,
      progress: this.progress(),
    };
  }

  progress(): number {
    if(!this.startEpoch || !this.endEpoch || !this.currentEpoch) return 0;
    const span = this.endEpoch - this.startEpoch;
    if(span <= 0) return 0;
    return Math.min(100, Math.max(0, ((this.currentEpoch - this.startEpoch)/span)*100));
  }

  setSpeed(speed: PlaybackSpeed){
    this.playbackSpeed = speed;
    this.scheduler.setSpeed(speed);
    if(this.isPlaying){
      this.pause();
      this.play();
    }
  }

  rewindTo(epoch: number){
    const quotes = this.getQuotesFn();
    if(!quotes.length) return;
    // find first index whose epoch >= epoch
    const targetIdx = quotes.findIndex(q => (q.DT ? q.DT.getTime() : Date.parse(q.Date+ 'Z')) >= epoch);
    const sliceIdx = targetIdx >=0 ? targetIdx : quotes.length -1;
    this.engine.resetSeries();
    this.engine.applyTickBatch(quotes.slice(0, sliceIdx+1), false);
    this.idx = sliceIdx;
    this.currentEpoch = epoch;
    this.startEpoch = (quotes[0].DT ? quotes[0].DT.getTime() : Date.parse(quotes[0].Date+'Z'));
    const last = quotes[quotes.length-1];
    this.endEpoch = last ? (last.DT ? last.DT.getTime() : Date.parse(last.Date+'Z')) : null;
    this.mode = 'replay';
    this.isPlaying = false;
  }

  play(){
    const quotes = this.getQuotesFn();
    if(!quotes.length) return;
    if(this.currentEpoch == null){
      this.rewindTo((quotes[0].DT ? quotes[0].DT.getTime() : Date.parse(quotes[0].Date+'Z')));
    }
    this.isPlaying = true;
    this.mode = 'replay';
    this._loop();
  }

  pause(){
    this.isPlaying = false;
    if(this.playbackTimer){ clearTimeout(this.playbackTimer); this.playbackTimer=undefined; }
    this.mode = 'paused';
  }

  stop(){
    this.pause();
    // Restore full live data to chart
    if (this.onRestoreLiveFn) {
      this.onRestoreLiveFn();
    } else {
      // Fallback: restore all quotes if no callback provided
      const quotes = this.getQuotesFn();
      if (quotes.length) {
        this.engine.resetSeries();
        this.engine.applyTickBatch(quotes, false);
      }
    }
    this.engine.clearReplayState();
    this.mode = 'live';
    this.currentEpoch = null;
    this.idx = 0;
  }

  stepForward(){
    const quotes = this.getQuotesFn();
    if(this.idx >= quotes.length -1) return;
    this.idx++;
    const q = quotes[this.idx];
    this.engine.applyTick(q, false);
    this.currentEpoch = (q.DT ? q.DT.getTime() : Date.parse(q.Date+'Z'));
  }

  stepBack(){
    const quotes = this.getQuotesFn();
    if(this.idx <=0) return;
    this.idx--;
    this.engine.resetSeries();
    this.engine.applyTickBatch(quotes.slice(0,this.idx+1), false);
    const q = quotes[this.idx];
    this.currentEpoch = (q.DT ? q.DT.getTime() : Date.parse(q.Date+'Z'));
  }

  private _loop(){
    if(!this.isPlaying) return;
    const quotes = this.getQuotesFn();
    if(this.idx >= quotes.length -1){ this.pause(); return; }
    const batchSize = this.scheduler.getBatchSize();
    const end = Math.min(quotes.length -1, this.idx + batchSize);
    const batch = quotes.slice(this.idx+1, end+1);
    this.engine.applyTickBatch(batch, true);
    this.idx = end;
    const last = batch[batch.length-1];
    this.currentEpoch = (last.DT ? last.DT.getTime() : Date.parse(last.Date+'Z'));
    this.playbackTimer = setTimeout(()=> this._loop(), this.scheduler.getFrameDelayMs());
  }
}
